import { Dialog, DialogBody, DialogClose, DialogContent, DialogTitle, DialogDescription } from '@/components/cms/ui/dialog';
import { IconButton } from '@/components/ui/icon-button';
import LureoButton, { type LureoButtonVariant } from '@/common/LureoButton';
import { XIcon } from '@phosphor-icons/react';
import { ReactNode } from 'react';

interface InfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children?: ReactNode;
  icon?: ReactNode;
  actionButton?: {
    label: string;
    onClick: () => void;
    variant?: LureoButtonVariant;
  };
  secondaryButton?: {
    label: string;
    onClick: () => void;
    variant?: LureoButtonVariant;
  };
}

// The dashboard's Download, Book a Call and Discord dialogs as production draws them: centred, icon on a raised disc, large title, wide pill buttons, no header or footer bar
export function InfoDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  icon,
  actionButton,
  secondaryButton
}: InfoDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* 448px wide, as production's */}
      <DialogContent hideCloseButton className="max-w-md bg-lureo-surface bg-linear-to-br from-lureo-neutral-subtle to-lureo-brand/5">
        <DialogBody className="p-6">
          {icon && (
            <div className="mb-4 flex justify-center">
              <div className="rounded-full bg-lureo-surface p-4 shadow-lureo-shadow-bar">
                {icon}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1.5 text-center">
            <DialogTitle className="text-lureo-24 font-bold leading-8 tracking-tight text-lureo-ink-strong">{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </div>

          {children}

          {(actionButton || secondaryButton) && (
            <div className="mt-4 flex justify-end gap-3">
              {actionButton && (
                <LureoButton
                  onClick={actionButton.onClick}
                  variant={actionButton.variant || 'secondary'}
                  suppressTooltip
                  className="h-12 flex-1 rounded-lureo-radius-button-pill px-6"
                >
                  {actionButton.label}
                </LureoButton>
              )}
              {secondaryButton && (
                <LureoButton
                  onClick={secondaryButton.onClick}
                  variant={secondaryButton.variant || 'outline-3'}
                  suppressTooltip
                  className="h-12 min-w-32 rounded-lureo-radius-button-pill px-6"
                >
                  {secondaryButton.label}
                </LureoButton>
              )}
            </div>
          )}
        </DialogBody>

        {/* Its close button 24px in from the corner, as production's, with no header bar to centre it in */}
        <DialogClose asChild>
          <IconButton label="Close" place="dialog" className="absolute right-2 top-2">
            <XIcon className="h-4 w-4" />
          </IconButton>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
